import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faCaretDown,
  faBars,
  faAnglesRight,
  faFilter,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';
import { useEffectiveOperators } from '@/hooks';
import { useDesignatedOperatorRelationsAll } from '@/hooks';
import { useUserPreferences } from '@/hooks';
import NO2ReportFilters from './NO2ReportFilters';
import NO3ReportFilters from './NO3ReportFilters';
import NO4ReportFilters from './NO4ReportFilters';
import CN54ReportFilters from './CN54ReportFilters';
import CN54bisReportFilters from './CN54bisReportFilters';
import CN56ReportFilters from './CN56ReportFilters';
import CN10ReportFilters from './CN10ReportFilters';
import CN19ReportFilters from './CN19ReportFilters';
import ENO1StatusFilters from './ENO1StatusFilters';
import E53StatusFilters from './E53StatusFilters';
import E09StatusFilters from './E09StatusFilters';
import E55StatusFilters from './E55StatusFilters';
import E55ComparisonFilters from './E55ComparisonFilters';
import AccuracyReportFilters from './AccuracyReportFilters';
import ItemsKgReportFilters from './ItemsKgReportFilters';
import IpcTdAccountingFilters from './IpcTdAccountingFilters';
import { PLAYGROUND_PPO } from '@/common/constants';
import {
  StyledContainer,
  MenuButton,
  ContentContainer,
  TitleSection,
  BreadcrumbChip,
  Title,
  FilterContainer,
  FilterButton,
  StyledSegmentedControl,
  StyledSegmentButton,
  StyledActionButton,
  StyledBookmarkButton,
  MobileFilterButton,
  MobileFilterDrawer,
  MobileFilterContent,
  MobileFilterHeader,
  MobileFilterActions,
} from './SectionBar.styled';

interface SegmentOption {
  id: string;
  label: string;
}

// Define PPOOption interface
interface PPOOption {
  id: string;
  label: string;
  hasLicense?: boolean;
}

export interface SectionBarProps {
  title: string;
  subtitle?: string;
  onToggleSidebar: () => void;
  isSidebarOpen?: boolean;
  onBookmarkClick?: () => void;
  onSubtitleClick?: () => void;
  module?: string; // Module name for cascading logic (e.g., 'eno1', 'e53', 'e55', 'e09')
  showFilters?: {
    status?: boolean;
    state?: boolean;
    type?: boolean;
    year?: boolean;
    ppo?: boolean;
    customNO2?: boolean;
    customNO3?: boolean;
    customNO4?: boolean;
    customCN54?: boolean;
    customCN54bis?: boolean;
    customCN56?: boolean;
    customCN10?: boolean;
    customCN19?: boolean;
    customENO1Status?: boolean;
    customE53Status?: boolean;
    customE09Status?: boolean;
    customE55Status?: boolean;
    customE55?: boolean;
    customAccuracy?: boolean;
    customItemsKg?: boolean;
    customIpcTdAccounting?: boolean;
    customStatus?: boolean;
  };
  // Custom Report props (supports NO2, NO3, CN54, CN10, and ENO1Status)
  customFilters?: {
    // Report filters (NO2, NO3, CN54)
    serviceTypes?: string[];
    mailFormats?: string[];
    accountingTypes?: string[];
    fromPeriod?: string;
    toPeriod?: string;
    includeIpkSummary?: boolean;
    direction?: string;
    year?: string;
    // Generic PPO filter (for NO4 and other reports)
    ppo?: string;
    ppoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    isPpoLoading?: boolean;
    onServiceTypesChange?: (values: string[]) => void;
    onMailFormatsChange?: (values: string[]) => void;
    onAccountingTypesChange?: (values: string[]) => void;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onIpkSummaryChange?: (checked: boolean) => void;
    onDirectionChange?: (direction: string) => void;
    onYearChange?: (year: string) => void;
    onPpoChange?: (ppo: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
    
    // CN10 specific filters
    mailCategories?: string[];
    type?: string;
    onMailCategoriesChange?: (values: string[]) => void;
    onTypeChange?: (type: string) => void;
    
    // CN56 specific filters
    groupBy?: string[];
    onGroupByChange?: (values: string[]) => void;
    
    // CN19 specific filters
    cn19Ppo?: string;
    cn19Year?: string;
    cn19Currency?: string;
    cn19PpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    cn19YearOptions?: string[];
    cn19IsPpoLoading?: boolean;
    onCn19PpoChange?: (value: string) => void;
    onCn19YearChange?: (value: string) => void;
    onCn19CurrencyChange?: (value: string) => void;
    onCn19EnterIbrsRates?: () => void;
    cn19IsProcessing?: boolean;
    
    // ENO1Status filters
    eno1StatusPpo?: string;
    eno1StatusYear?: string;
    eno1StatusDirection?: string;
    eno1StatusStatus?: string;
    eno1StatusPpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    eno1StatusYearOptions?: string[];
    eno1StatusIsPpoLoading?: boolean;
    onEno1StatusPpoChange?: (value: string) => void;
    onEno1StatusYearChange?: (value: string) => void;
    onEno1StatusDirectionChange?: (value: string) => void;
    onEno1StatusStatusChange?: (value: string) => void;
    onEno1StatusSubmit?: () => void;
    onEno1StatusReset?: () => void;
    eno1StatusIsSubmitting?: boolean;

    // E09Status filters
    e09StatusPpo?: string;
    e09StatusYear?: string;
    e09StatusDirection?: string;
    e09StatusStatus?: string;
    e09StatusPpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    e09StatusYearOptions?: string[];
    e09StatusIsPpoLoading?: boolean;
    onE09StatusPpoChange?: (value: string) => void;
    onE09StatusYearChange?: (value: string) => void;
    onE09StatusDirectionChange?: (value: string) => void;
    onE09StatusStatusChange?: (value: string) => void;
    onE09StatusSubmit?: () => void;
    onE09StatusReset?: () => void;
    e09StatusIsSubmitting?: boolean;

    e55StatusPpo?: string;
    e55StatusYear?: string;
    e55StatusDirection?: string;
    e55StatusStatus?: string;
    e55StatusPpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    e55StatusYearOptions?: string[];
    e55StatusIsPpoLoading?: boolean;
    onE55StatusPpoChange?: (value: string) => void;
    onE55StatusYearChange?: (value: string) => void;
    onE55StatusDirectionChange?: (value: string) => void;
    onE55StatusStatusChange?: (value: string) => void;
    onE55StatusSubmit?: () => void;
    onE55StatusReset?: () => void;
    e55StatusIsSubmitting?: boolean;
    
    // E53Status filters
    e53StatusPpo?: string;
    e53StatusYear?: string;
    e53StatusDirection?: string;
    e53StatusStatus?: string;
    e53StatusPpoOptions?: Array<{ id: string; name: string; disabled: boolean }>;
    e53StatusYearOptions?: string[];
    e53StatusIsPpoLoading?: boolean;
    onE53StatusPpoChange?: (value: string) => void;
    onE53StatusYearChange?: (value: string) => void;
    onE53StatusDirectionChange?: (value: string) => void;
    onE53StatusStatusChange?: (value: string) => void;
    onE53StatusSubmit?: () => void;
    onE53StatusReset?: () => void;
    e53StatusIsSubmitting?: boolean;
    
    // E55Comparison filters
    mscValues?: string[];
    onMSCChange?: (values: string[]) => void;
    
    // Accuracy Report filters
    useVersions?: string;
    displayReportFor?: string;
    detailsOption?: string;
    onUseVersionsChange?: (version: string) => void;
    onDisplayReportForChange?: (display: string) => void;
    onDetailsOptionChange?: (option: string) => void;
    
    // IpcTdAccounting filters
    fromYear?: string;
    toYear?: string;
    onFromYearChange?: (year: string) => void;
    onToYearChange?: (year: string) => void;
    
    // Custom status filter
    status?: string;
    onStatusChange?: (status: string) => void;
  };
}

// Module color mapping
const MODULE_COLORS: Record<string, string> = {
  'eNO1 Sampling': '#00ACE7',
  'eNO1 Message': '#00ACE7',
  'e53 Sampling': '#063142',
  'e53 Message': '#063142',
  'Sampling Planning': '#6A848E',
  'Sampling Planning Message': '#6A848E',
  'e55 Weights': '#49A942',
  'e55 Message': '#49A942', 
  'e09 IBRS': '#575A9C',
  'e09 Message': '#575A9C',
  'IPC TD Accounting': '#FAA918',
  'IPC Message': '#FAA918',
  'TD Accounting': '#93620A',
  'UPU TD Accounting': '#93620A', // Assuming this is the same as TD Accounting
};

// Function to get the module color or return a default
const getModuleColor = (moduleName: string): string => {
  // If there's an exact match, use it first
  if (MODULE_COLORS[moduleName]) {
    return MODULE_COLORS[moduleName];
  }
  
  // Check for partial matches
  const lowerModuleName = moduleName.toLowerCase();
  
  if (lowerModuleName.includes('eno1')) {
    return '#00ACE7'; // eNO1 color
  }
  
  if (lowerModuleName.includes('e53')) {
    return '#063142'; // e53 color
  }
  
  if (lowerModuleName.includes('sampling planning')) {
    return '#6A848E'; // Sampling Planning color
  }
  
  if (lowerModuleName.includes('e55')) {
    return '#49A942'; // e55 color
  }
  
  if (lowerModuleName.includes('e09')) {
    return '#575A9C'; // e09 color
  }
  
  if (lowerModuleName.includes('ipc td') || lowerModuleName.includes('ipc message')) {
    return '#FAA918'; // IPC TD Accounting color
  }
  
  if (lowerModuleName.includes('td accounting') || lowerModuleName.includes('upu td')) {
    return '#93620A'; // TD Accounting/UPU TD color
  }
  
  return '#00B6BA'; // Default color if no match found
};

const SectionBar: React.FC<SectionBarProps> = ({
  title,
  subtitle,
  onToggleSidebar,
  isSidebarOpen,
  onBookmarkClick,
  onSubtitleClick,
  module = 'eno1', // Default to 'eno1' for backward compatibility
  showFilters = {}, // Default to an empty object if not provided
  customFilters,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine which filters to show
  const shouldShowFilters = Object.values(showFilters).some(Boolean);
  const shouldShowStatus = showFilters.status || showFilters.state || false; // Support both status and state
  const shouldShowType = showFilters.type || false;
  const shouldShowYear = showFilters.year || false;
  const shouldShowPpo = showFilters.ppo || false;
  const shouldShowCustomNO2 = showFilters.customNO2 || false;
  const shouldShowCustomNO3 = showFilters.customNO3 || false;
  const shouldShowCustomNO4 = showFilters.customNO4 || false;
  const shouldShowCustomCN54 = showFilters.customCN54 || false;
  const shouldShowCustomCN54bis = showFilters.customCN54bis || false;
  const shouldShowCustomCN56 = showFilters.customCN56 || false;
  const shouldShowCustomCN10 = showFilters.customCN10 || false;
  const shouldShowCustomCN19 = showFilters.customCN19 || false;
  const shouldShowCustomENO1Status = showFilters.customENO1Status || false;
  const shouldShowCustomE53Status = showFilters.customE53Status || false;
  const shouldShowCustomE09Status = showFilters.customE09Status || false;
  const shouldShowCustomE55Status = showFilters.customE55Status || false;
  const shouldShowCustomE55 = showFilters.customE55 || false;
  const shouldShowCustomAccuracy = showFilters.customAccuracy || false;
  const shouldShowCustomItemsKg = showFilters.customItemsKg || false;
  const shouldShowCustomIpcTdAccounting = showFilters.customIpcTdAccounting || false;
  const shouldShowCustomStatus = showFilters.customStatus || false;

  // Fetch effective operators (Settings ∩ Preferences) scoped by module
  const {
    operators: effectiveOperators,
    isLoading: isPposLoading,
    error: pposError,
  } = useEffectiveOperators(undefined, module, shouldShowPpo);

  // Get all designated operator relations for cascading logic
  const {
    data: allRelationsData,
    isLoading: isRelationsLoading,
    error: relationsError,
  } = useDesignatedOperatorRelationsAll(module);

  // ─── User preferences: detect whether customisations exist for this module ─
  // This mirrors the fallback logic in useModuleOperators to prevent wrong
  // operators appearing when the user has preferences for other modules but
  // not for the current one.
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  /** True when the user has ≥1 enabled preference for THIS specific module. */
  const hasModulePreferences = React.useMemo(
    () =>
      preferences.some((p) =>
        p.modules.some((m) => m.name === module && m.enabled),
      ),
    [preferences, module],
  );

  /** True when the user has ANY preference with at least one enabled module. */
  const hasAnyPreferences = React.useMemo(
    () => preferences.some((p) => p.modules.some((m) => m.enabled)),
    [preferences],
  );

  // ─── Settings operators fallback ────────────────────────────────────────────
  // Derived from allRelationsData — only used when the user is pristine (no
  // preferences at all) and the backend effective-operators list is empty.
  const settingsOperators = React.useMemo(() => {
    if (!allRelationsData?.byTargetDO) return [] as string[];
    const ops: string[] = [];
    Object.entries(allRelationsData.byTargetDO).forEach(([targetDO, relations]) => {
      if (relations.some((r) => r.modules.some((m) => m.name === module && m.enabled))) {
        ops.push(targetDO);
      }
    });
    return ops.sort();
  }, [allRelationsData, module]);

  // ─── Resolved operators (with fallback) ─────────────────────────────────────
  // Priority:
  //  1. User has ≥1 enabled preference for THIS module → use effectiveOperators.
  //  2. User has preferences for OTHER modules but NONE for this module
  //     → empty list (the user deliberately has no customisation for this module).
  //  3. User has NO preferences at all (pristine)
  //     → fall back to Settings operators so behaviour matches pre-CR-04.
  const enabledTargetDOs = React.useMemo(() => {
    // Case 1: user has active preferences for this module → trust backend
    if (hasModulePreferences) return effectiveOperators;
    // Case 2: user has preferences but not for this module → empty
    if (hasAnyPreferences) return [] as string[];
    // Case 3: no preferences at all (pristine) → fall back to Settings
    return effectiveOperators.length > 0 ? effectiveOperators : settingsOperators;
  }, [hasModulePreferences, hasAnyPreferences, effectiveOperators, settingsOperators]);

  // Get available years for enabled DOs only
  const availableYears = React.useMemo(() => {
    // If the user has ≥1 enabled preference for this module → show only years
    // from those preferences (mirrors useModuleOperators logic).
    if (hasModulePreferences) {
      const years = new Set<number>();
      preferences.forEach((p) => {
        if (p.modules.some((m) => m.name === module && m.enabled)) {
          years.add(p.accountingYear);
        }
      });
      return Array.from(years).sort((a, b) => b - a);
    }

    // User has preferences for other modules but not this one → empty
    if (hasAnyPreferences) return [] as number[];

    // No preferences → derive from Settings relations
    if (!allRelationsData?.byYear) return [];
    
    const years: number[] = [];
    
    Object.entries(allRelationsData.byYear).forEach(([year, relations]) => {
      // Check if any relation for this year has an enabled DO with the specified module
      const hasEnabledDO = relations.some(relation => 
        enabledTargetDOs.includes(relation.targetDO) &&
        relation.modules.some(mod => 
          mod.name === module && mod.enabled === true
        )
      );
      
      if (hasEnabledDO) {
        years.push(parseInt(year));
      }
    });
    
    return years.sort((a, b) => b - a); // Sort descending (most recent first)
  }, [hasModulePreferences, hasAnyPreferences, preferences, allRelationsData, enabledTargetDOs, module]);

  // Get filter values from URL query parameters (declare these functions first)
  const getStatusFromUrl = (): string => {
    // Check for 'state' parameter first (new), then fallback to 'status' (legacy)
    return searchParams.get('state') || searchParams.get('status') || 'all';
  };

  const getTypeFromUrl = (): string => {
    const types = searchParams.getAll('type');
    if (!types.length) return 'all';
    if (types.includes('all')) return 'all';
    return types[0]; // Just take the first one for the button UI
  };

  const getYearFromUrl = (): string => {
    return searchParams.get('year') || 'all';
  };

  const getPpoFromUrl = (): string => {
    return searchParams.get('ppo') || 'ALL';
  };

  // Get selected year from URL
  const selectedYear = getYearFromUrl();
  const selectedPpo = getPpoFromUrl();

  // Get target DOs for selected year range (cascading logic)
  const targetDOsForYear = React.useMemo(() => {
    if (!allRelationsData?.byTargetDO || selectedYear === 'all') return enabledTargetDOs;
    
    const filteredDOs: string[] = [];
    const yearNum = parseInt(selectedYear);
    
    enabledTargetDOs.forEach(targetDO => {
      const relations = allRelationsData.byTargetDO[targetDO];
      if (!relations) return;
      
      // Get years where this DO has the specified module enabled
      const availableYears = relations
        .filter(relation => 
          relation.modules.some(mod => 
            mod.name === module && mod.enabled === true
          )
        )
        .map(relation => relation.year)
        .sort((a, b) => a - b); // Sort ascending to get min/max
      
      if (availableYears.length === 0) return;
      
      const minYear = availableYears[0];
      const maxYear = availableYears[availableYears.length - 1];
      
      // Check if the selected year is within the DO's available range
      if (yearNum >= minYear && yearNum <= maxYear) {
        filteredDOs.push(targetDO);
      }
    });
    
    return filteredDOs.sort();
  }, [selectedYear, allRelationsData, enabledTargetDOs, module]);

  // Get years filtered by selected DO (cascading logic)
  const yearsForSelectedDO = React.useMemo(() => {
    if (selectedPpo === 'ALL' || !allRelationsData?.byTargetDO?.[selectedPpo]) return availableYears;
    
    const relations = allRelationsData.byTargetDO[selectedPpo];
    const years = relations
      .filter(relation => 
        relation.modules.some(mod => 
          mod.name === module && mod.enabled === true
        )
      )
      .map(relation => relation.year)
      .sort((a, b) => b - a); // Sort descending (most recent first)
    
    // When the user has preferences for this module, constrain the years
    // to only those present in the preference-based availableYears list.
    if (hasModulePreferences) {
      const allowedYears = new Set(availableYears);
      return years.filter(y => allowedYears.has(y));
    }
    
    return years;
  }, [selectedPpo, allRelationsData, availableYears, module, hasModulePreferences]);

  // Map the designated operators to PPO options format with cascading logic
  const ppoOptions = React.useMemo<PPOOption[]>(() => {
    // Use custom PPO options if provided (for NO4 and other custom reports)
    if (customFilters?.ppoOptions) {
      const options: PPOOption[] = [];
      
      // Don't add "All" option for E55Comparison filters or NO4 reports
      if (!showFilters?.customE55 && !showFilters?.customNO4) {
        options.push({ id: 'ALL', label: 'All' });
      }
      
      // Map custom options to PPOOption format
      customFilters.ppoOptions
        .filter((operator) => !operator.disabled /*&& operator.id !== PLAYGROUND_PPO.id*/)
        .forEach((operator) => {
          options.push({
            id: operator.id,
            label: operator.name,
            hasLicense: true, // Assume custom options have license info
          });
        });

      // Add PLG (Playground) option — special case, not from API
      // options.push(PLAYGROUND_PPO);

      return options;
    }
    
    // Default behavior: use designated operators data with cascading logic
    const options: PPOOption[] = [];
    
    // Don't add "All" option for E55Comparison filters or NO4 reports
    if (!showFilters?.customE55 && !showFilters?.customNO4) {
      options.push({ id: 'ALL', label: 'All' });
    }

    // Use cascaded DOs if year filter is enabled, otherwise use all enabled DOs
    const dosToShow = shouldShowYear && selectedYear !== 'all' 
      ? targetDOsForYear 
      : enabledTargetDOs;

    dosToShow.forEach((targetDO) => {
      options.push({
        id: targetDO,
        label: targetDO,
        hasLicense: true, // All enabled target DOs have licenses
      });
    });

    // Add PLG (Playground) option — special case, not from API
    // options.push(PLAYGROUND_PPO);

    return options;
  }, [showFilters?.customE55, customFilters?.ppoOptions, shouldShowYear, selectedYear, targetDOsForYear, enabledTargetDOs]);

  // Get the appropriate module color if subtitle is provided
  const moduleColor = subtitle ? getModuleColor(subtitle) : undefined;

  // For PPO dropdown menu handling
  const [ppoAnchorEl, setPpoAnchorEl] = React.useState<null | HTMLElement>(null);
  const isPpoMenuOpen = Boolean(ppoAnchorEl);

  // For year dropdown menu handling
  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(null);
  const isYearMenuOpen = Boolean(yearAnchorEl);

  // For NO2Report period dropdown menu handling
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);

  // For custom NO2Report filters dropdown menu handling
  const [serviceTypesAnchorEl, setServiceTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mailFormatsAnchorEl, setMailFormatsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [accountingTypesAnchorEl, setAccountingTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const isServiceTypesMenuOpen = Boolean(serviceTypesAnchorEl);
  const isMailFormatsMenuOpen = Boolean(mailFormatsAnchorEl);
  const isAccountingTypesMenuOpen = Boolean(accountingTypesAnchorEl);

  // Generate years with cascading logic based on selected DO
  const years = React.useMemo(() => {
    // Use cascaded years if DO filter is enabled and a DO is selected, otherwise use all available years
    const yearsToShow = shouldShowPpo && selectedPpo !== 'ALL' 
      ? yearsForSelectedDO 
      : availableYears;

    // Convert to string array and add 'all' option
    return ['all', ...yearsToShow.map(year => year.toString())];
  }, [shouldShowPpo, selectedPpo, yearsForSelectedDO, availableYears]);

  // Generate year-quarter options with cascading logic based on available years
  const yearQuarterOptions = React.useMemo(() => {
    // Use cascaded year-quarter options from customFilters if provided (for NO2 and other reports)
    if (customFilters?.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Default behavior: generate year-quarter options based on available years
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    
    // Use cascaded years if DO filter is enabled and a DO is selected, otherwise use all available years
    const yearsToShow = shouldShowPpo && selectedPpo !== 'ALL' 
      ? yearsForSelectedDO 
      : availableYears;
    
    // If no years available, fall back to all years from 2008 to current
    const finalYears = yearsToShow.length > 0 ? yearsToShow : (() => {
      const currentYear = new Date().getFullYear();
      const allYears: number[] = [];
      for (let year = currentYear; year >= 2008; year--) {
        allYears.push(year);
      }
      return allYears;
    })();
    
    finalYears.forEach(year => {
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    });
    
    return options;
  }, [customFilters?.yearQuarterOptions, shouldShowPpo, selectedPpo, yearsForSelectedDO, availableYears]);

  // Local state for temporary filter values (before Apply is clicked)
  const [tempStatusId, setTempStatusId] = React.useState<string>(getStatusFromUrl());
  const [tempTypeId, setTempTypeId] = React.useState<string>(getTypeFromUrl());
  const [tempYearId, setTempYearId] = React.useState<string>(getYearFromUrl());
  const [tempPpoId, setTempPpoId] = React.useState<string>(customFilters?.ppo || getPpoFromUrl());
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState<boolean>(false);
  const [hasUnappliedChanges, setHasUnappliedChanges] = React.useState<boolean>(false);

  // Update tempPpoId when custom PPO value changes
  React.useEffect(() => {
    if (customFilters?.ppo !== undefined) {
      setTempPpoId(customFilters.ppo);
    }
  }, [customFilters?.ppo]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update local state when URL parameters change
  React.useEffect(() => {
    // Check if there are any params in the URL
    const hasParams = searchParams.toString().length > 0;

    if (!hasParams) {
      // If no params, use the default values
      setTempStatusId('all');
      setTempTypeId('all');
      setTempYearId('all');
      setTempPpoId('ALL');
      setHasUnappliedChanges(false);
      return;
    }

    const urlStatus = getStatusFromUrl();
    const urlType = getTypeFromUrl();
    const urlYear = getYearFromUrl();
    const urlPpo = getPpoFromUrl();

    setTempStatusId(urlStatus);
    setTempTypeId(urlType);
    setTempYearId(urlYear);
    setTempPpoId(urlPpo);
    setHasUnappliedChanges(false);
  }, [searchParams]);

  const statusOptions: SegmentOption[] = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'completed', label: 'Completed' },
  ];

  // Custom status options for samples
  const customStatusOptions: SegmentOption[] = [
    { id: 'all', label: 'All' },
    { id: 'valid', label: 'Valid' },
    { id: 'amended', label: 'Amended' },
  ];

  const typeOptions: SegmentOption[] = [
    { id: 'all', label: 'All' },
    { id: 'inbound', label: 'Inbound' },
    { id: 'outbound', label: 'Outbound' },
  ];

  // Custom NO2Report filter options
  const serviceTypeOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'B', label: 'B - SAL Non Priority' },
    { id: 'C', label: 'C - Surface Non-Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  const mailFormatOptions = [
    { id: 'P', label: 'P - Letters' },
    { id: 'Q', label: 'Q - Flats' },
    { id: 'R', label: 'R - Packets' },
    { id: 'X', label: 'X - PDF Manual' },
    { id: 'Y', label: 'Y - Journals' },
    { id: 'Z', label: 'Z - Bilateral' },
  ];

  const accountingTypeOptions = [
    { id: 'G', label: 'Gross' },
    { id: 'N', label: 'Net' },
  ];

  const handleStatusChange = (id: string) => {
    setTempStatusId(id);
    setHasUnappliedChanges(true);
  };

  const handleTypeChange = (id: string) => {
    setTempTypeId(id);
    setHasUnappliedChanges(true);
  };

  const handlePpoClick = (event: React.MouseEvent<HTMLElement>) => {
    setPpoAnchorEl(event.currentTarget);
  };

  const handlePpoMenuClose = () => {
    setPpoAnchorEl(null);
  };

  const handlePpoChange = (ppo: string) => {
    // Enhanced cascading logic: Clear year if the selected DO doesn't have data for the currently selected year
    if (ppo !== 'ALL' && tempYearId !== 'all') {
      const currentYear = tempYearId;
      const relationData = allRelationsData?.byTargetDO?.[ppo];
      
      if (relationData) {
        // Get years where this DO has the specified module enabled
        const availableYears = relationData
          .filter(relation => 
            relation.modules.some(mod => 
              mod.name === module && mod.enabled === true
            )
          )
          .map(relation => relation.year)
          .sort((a, b) => a - b); // Sort ascending to get min/max
        
        if (availableYears.length === 0) {
          setTempYearId('all');
        } else {
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const currentYearNum = parseInt(currentYear);
          
          // Check if the current year is within the DO's available range
          if (currentYearNum < minYear || currentYearNum > maxYear) {
            setTempYearId('all');
          }
        }
      } else {
        // If no relation data found for this DO, clear the year
        setTempYearId('all');
      }
    }
    
    setTempPpoId(ppo);
    setHasUnappliedChanges(true);
    handlePpoMenuClose();
    
    // If custom filters are shown (no Apply button), immediately update URL
    if (shouldShowCustomNO3 || shouldShowCustomNO4 || shouldShowCustomCN10 || shouldShowCustomCN54 || shouldShowCustomCN54bis || shouldShowCustomCN56 || shouldShowCustomCN19 || shouldShowCustomNO2 || shouldShowCustomIpcTdAccounting) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ppo');
      newParams.append('ppo', ppo);
      setSearchParams(newParams);
      setHasUnappliedChanges(false);
      
      // If custom PPO change handler is provided, call it
      if (customFilters?.onPpoChange) {
        customFilters.onPpoChange(ppo === 'ALL' ? '' : ppo);
      }
    }
  };

  const handleYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setYearAnchorEl(event.currentTarget);
  };

  const handleYearMenuClose = () => {
    setYearAnchorEl(null);
  };

  const handleYearChange = (year: string) => {
    // Enhanced cascading logic: Clear DO if the selected DO doesn't have data for the newly selected year
    if (year !== 'all' && tempPpoId !== 'ALL') {
      const yearNum = parseInt(year);
      const relationData = allRelationsData?.byTargetDO?.[tempPpoId];
      
      if (relationData) {
        // Get years where this DO has the specified module enabled
        const availableYears = relationData
          .filter(relation => 
            relation.modules.some(mod => 
              mod.name === module && mod.enabled === true
            )
          )
          .map(relation => relation.year)
          .sort((a, b) => a - b); // Sort ascending to get min/max
        
        if (availableYears.length === 0) {
          setTempPpoId('ALL');
        } else {
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          
          // Check if the selected year is within the DO's available range
          if (yearNum < minYear || yearNum > maxYear) {
            setTempPpoId('ALL');
          }
        }
      } else {
        // If no relation data found for this DO, clear the DO
        setTempPpoId('ALL');
      }
    }
    
    setTempYearId(year);
    setHasUnappliedChanges(true);
    handleYearMenuClose();
    
    // If custom filters are shown (no Apply button), immediately update URL and call handler
    if (shouldShowCustomNO3 || shouldShowCustomNO4 || shouldShowCustomCN10 || shouldShowCustomCN54 || shouldShowCustomCN54bis || shouldShowCustomCN56 || shouldShowCustomCN19 || shouldShowCustomNO2 || shouldShowCustomIpcTdAccounting) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('year');
      newParams.append('year', year === 'all' ? 'all' : year);
      setSearchParams(newParams);
      setHasUnappliedChanges(false);
      
      // If custom year change handler is provided, call it
      if (customFilters?.onYearChange) {
        customFilters.onYearChange(year === 'all' ? '' : year);
      }
    }
  };

  // NO2Report period handlers
  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    if (customFilters?.onFromPeriodChange) {
      customFilters.onFromPeriodChange(period);
    }
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    if (customFilters?.onToPeriodChange) {
      customFilters.onToPeriodChange(period);
    }
    handleToPeriodMenuClose();
  };

  // Custom NO2Report filter handlers
  const handleServiceTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServiceTypesAnchorEl(event.currentTarget);
  };

  const handleServiceTypesMenuClose = () => {
    setServiceTypesAnchorEl(null);
  };

  const handleMailFormatsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMailFormatsAnchorEl(event.currentTarget);
  };

  const handleMailFormatsMenuClose = () => {
    setMailFormatsAnchorEl(null);
  };

  const handleAccountingTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountingTypesAnchorEl(event.currentTarget);
  };

  const handleAccountingTypesMenuClose = () => {
    setAccountingTypesAnchorEl(null);
  };

  const handleServiceTypeToggle = (value: string) => {
    if (customFilters?.onServiceTypesChange) {
      const currentValues = customFilters.serviceTypes || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      customFilters.onServiceTypesChange(newValues);
    }
  };

  const handleMailFormatToggle = (value: string) => {
    if (customFilters?.onMailFormatsChange) {
      const currentValues = customFilters.mailFormats || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      customFilters.onMailFormatsChange(newValues);
    }
  };

  const handleAccountingTypeToggle = (value: string) => {
    if (customFilters?.onAccountingTypesChange) {
      const currentValues = customFilters.accountingTypes || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      customFilters.onAccountingTypesChange(newValues);
    }
  };

  const handleToggleMobileFilter = () => {
    setIsMobileFilterOpen((prev) => !prev);
  };

  const handleCloseMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  const handleApplyFilters = () => {
    // Create a new URLSearchParams object based on current params
    const newParams = new URLSearchParams(searchParams);

    // Update state parameter if status filter is shown
    if (shouldShowStatus && tempStatusId) {
      newParams.delete('status'); // Remove legacy parameter
      newParams.delete('state');  // Remove current parameter
      if (tempStatusId !== 'all') {
        newParams.append('state', tempStatusId);
      } else {
        newParams.append('state', 'all');
      }
    }

    // Update type parameter if type filter is shown
    if (shouldShowType && tempTypeId) {
      newParams.delete('type');
      if (tempTypeId !== 'all') {
        newParams.append('type', tempTypeId);
      } else {
        newParams.append('type', 'all');
      }
    }

    // Update year parameter if year filter is shown
    if (shouldShowYear && tempYearId) {
      newParams.delete('year');
      if (tempYearId !== 'all') {
        newParams.append('year', tempYearId);
      } else {
        newParams.append('year', 'all');
      }
    }

    // Update PPO parameter if PPO filter is shown - now single select
    if (shouldShowPpo) {
      newParams.delete('ppo');
      newParams.append('ppo', tempPpoId);
    }

    // Update the URL
    setSearchParams(newParams);

    if (isMobile) {
      handleCloseMobileFilter();
    }

    setHasUnappliedChanges(false);
  };

  const handleResetFilters = () => {
    // Reset temporary filter states
    setTempStatusId('all');
    setTempTypeId('all');
    setTempYearId('all');
    setTempPpoId('ALL');

    // Create empty params to clear all filters
    const newParams = new URLSearchParams();

    // Update the URL with cleared filters
    setSearchParams(newParams);

    if (isMobile) {
      handleCloseMobileFilter();
    }

    setHasUnappliedChanges(false);
  };

  // Common text styles for Apply button
  const applyButtonTextStyle = {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '100%',
    letterSpacing: '0%',
    textTransform: 'uppercase',
  };

  const renderFilterContent = () => (
    <>
      {shouldShowStatus && (
        <StyledSegmentedControl className={isMobile ? '' : 'primary-filter'}>
          {statusOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={tempStatusId === option.id}
              onClick={() => handleStatusChange(option.id)}
              sx={{ color: tempStatusId === option.id ? '#ffffff' : '#00B6BA' }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      )}

      {shouldShowType && (
        <StyledSegmentedControl className={isMobile ? '' : 'secondary-filter'}>
          {typeOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={tempTypeId === option.id}
              onClick={() => handleTypeChange(option.id)}
              sx={{ color: tempTypeId === option.id ? '#ffffff' : '#00B6BA' }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      )}

      {!isMobile && shouldShowFilters && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <StyledActionButton
            onClick={handleApplyFilters}
            sx={{
              opacity: hasUnappliedChanges ? 1 : 0.7,
              cursor: hasUnappliedChanges ? 'pointer' : 'default',
            }}
          >
            <Typography sx={applyButtonTextStyle}>Apply</Typography>
            <FontAwesomeIcon className='button-icon' icon={faFilter} style={{ display: 'none' }} />
          </StyledActionButton>
          <StyledActionButton onClick={handleResetFilters}>
            <Typography>Reset</Typography>
            <FontAwesomeIcon className='button-icon' icon={faXmark} style={{ display: 'none' }} />
          </StyledActionButton>
        </Box>
      )}
    </>
  );

  // Get the displayed year label (for the button)
  const getYearLabel = () => {
    return tempYearId === 'all' ? 'All Years' : tempYearId;
  };

  // Get the displayed PPO label (for the button)
  const getPpoLabel = () => {
    if (tempPpoId === 'ALL') {
      return 'All';
    }
    
    const selected = ppoOptions.find((option) => option.id === tempPpoId);
    return selected ? selected.label : 'All';
  };

  // Get the displayed period labels for NO2Report
  const getFromPeriodLabel = () => {
    if (!customFilters?.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find((opt: { value: string; label: string; year: string; quarter: string }) => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters?.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find((opt: { value: string; label: string; year: string; quarter: string }) => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  // Get custom NO2Report filters from URL
  const getCustomFilters = () => {
    const serviceTypes = searchParams.get('serviceTypes');
    const mailFormats = searchParams.get('mailFormats');
    const accountingTypes = searchParams.get('accountingTypes');
    const fromPeriod = searchParams.get('fromPeriod');
    const toPeriod = searchParams.get('toPeriod');

    const filters = [];

    if (serviceTypes) {
      filters.push(`Service Types: ${serviceTypes}`);
    }

    if (mailFormats) {
      filters.push(`Mail Formats: ${mailFormats}`);
    }

    if (accountingTypes) {
      filters.push(`Accounting Types: ${accountingTypes}`);
    }

    if (fromPeriod) {
      filters.push(`From: ${fromPeriod}`);
    }

    if (toPeriod) {
      filters.push(`To: ${toPeriod}`);
    }

    return filters;
  };

  const customFilterDisplays = getCustomFilters();

  // Helper functions to get display labels for custom filters
  const getServiceTypesLabel = () => {
    const selected = customFilters?.serviceTypes || [];
    if (selected.length === 0) return 'Select Service Types';
    if (selected.length === serviceTypeOptions.length) return 'All Service Types';
    return `${selected.length} Service Types`;
  };

  const getMailFormatsLabel = () => {
    const selected = customFilters?.mailFormats || [];
    if (selected.length === 0) return 'Select Mail Formats';
    if (selected.length === mailFormatOptions.length) return 'All Mail Formats';
    return `${selected.length} Mail Formats`;
  };

  const getAccountingTypesLabel = () => {
    const selected = customFilters?.accountingTypes || [];
    if (selected.length === 0) return 'Select Accounting Types';
    if (selected.length === accountingTypeOptions.length) return 'All Accounting Types';
    return `${selected.length} Accounting Types`;
  };

  return (
    <>
      <StyledContainer isSidebarOpen={isSidebarOpen}>
        <MenuButton $isOpen={isSidebarOpen} onClick={onToggleSidebar}>
          <FontAwesomeIcon icon={isSidebarOpen ? faAnglesRight : faBars} />
        </MenuButton>

        <ContentContainer>
          <TitleSection>
            {subtitle && (
              <BreadcrumbChip
                onClick={onSubtitleClick}
                sx={{
                  backgroundColor: moduleColor,
                  '&:hover': {
                    backgroundColor: moduleColor ? `${moduleColor}DD` : undefined, // Add transparency on hover
                  },
                }}
              >
                <Typography sx={{ color: '#FFFFFF' }}>{subtitle}</Typography>
              </BreadcrumbChip>
            )}
            <Title>{title}</Title>
            {/* Anchor for contextual help "?" icons (HelpIconInjector targets this
                so help appears to the right of the page title). */}
            <Box
              data-testid="section-bar-help-anchor"
              sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}
            />
          </TitleSection>

          {shouldShowFilters && (
            <FilterContainer>
              {shouldShowPpo && (
                <>
                  <FilterButton onClick={handlePpoClick}>
                    <span className='label'>DO</span>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        {getPpoLabel()}
                      </Typography>
                      <FontAwesomeIcon icon={faChevronDown} size='sm' />
                    </Box>
                  </FilterButton>

                  {/* PPO Dropdown Menu - Now with radio selection instead of checkboxes */}
                  <Menu
                    anchorEl={ppoAnchorEl}
                    open={isPpoMenuOpen}
                    onClose={handlePpoMenuClose}
                    PaperProps={{
                      style: {
                        maxHeight: 300,
                        width: '150px',
                      },
                    }}
                  >
                    {(customFilters?.isPpoLoading ?? isPposLoading) ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} color='info' />
                      </Box>
                    ) : (!customFilters?.ppoOptions && pposError) ? (
                      <MenuItem disabled>Error loading PPOs</MenuItem>
                    ) : (
                      ppoOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          onClick={() => handlePpoChange(option.id)}
                          selected={tempPpoId === option.id}
                          sx={{
                            fontSize: '14px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '4px 16px',
                            fontWeight: tempPpoId === option.id ? 700 : 400,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </>
              )}

              {shouldShowYear && !shouldShowCustomNO2 && !shouldShowCustomNO3 && !shouldShowCustomNO4 && !shouldShowCustomCN54 && !shouldShowCustomCN54bis && !shouldShowCustomCN56 && (
                <>
                  <FilterButton className='secondary-filter' onClick={handleYearClick}>
                    <span className='label'>Year</span>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        {getYearLabel()}
                      </Typography>
                      <FontAwesomeIcon icon={faChevronDown} size='sm' />
                    </Box>
                  </FilterButton>

                  {/* Year Dropdown Menu */}
                  <Menu
                    anchorEl={yearAnchorEl}
                    open={isYearMenuOpen}
                    onClose={handleYearMenuClose}
                    PaperProps={{
                      style: {
                        maxHeight: 300,
                        width: '150px',
                      },
                    }}
                  >
                    {years.map((year) => (
                      <MenuItem
                        key={year}
                        onClick={() => handleYearChange(year)}
                        selected={tempYearId === year}
                        sx={{
                          fontWeight: tempYearId === year ? 700 : 400,
                          fontSize: '14px',
                        }}
                      >
                        {year === 'all' ? 'All Years' : year}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              {/* Custom Report Filters */}
              {shouldShowCustomNO2 && !isMobile && customFilters && (
                <NO2ReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomNO3 && !isMobile && customFilters && (
                <NO3ReportFilters customFilters={{
                  ...customFilters,
                  yearOptions: years.filter(year => year !== 'all')
                }} />
              )}

              {shouldShowCustomNO4 && !isMobile && customFilters && (
                <NO4ReportFilters customFilters={{
                  ...customFilters,
                  yearOptions: years.filter(year => year !== 'all')
                }} />
              )}

              {shouldShowCustomCN54 && !isMobile && customFilters && (
                <CN54ReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomCN54bis && !isMobile && customFilters && (
                <CN54bisReportFilters customFilters={{
                  ...customFilters,
                  yearOptions: years.filter(year => year !== 'all')
                }} />
              )}

              {shouldShowCustomCN56 && !isMobile && customFilters && (
                <CN56ReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomCN10 && !isMobile && customFilters && (
                <CN10ReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomCN19 && !isMobile && customFilters && (
                <CN19ReportFilters customFilters={{
                  ppo: customFilters.cn19Ppo,
                  year: customFilters.cn19Year,
                  currency: customFilters.cn19Currency,
                  ppoOptions: customFilters.cn19PpoOptions,
                  yearOptions: customFilters.cn19YearOptions || years.filter(year => year !== 'all'),
                  isPpoLoading: customFilters.cn19IsPpoLoading,
                  onPpoChange: customFilters.onCn19PpoChange,
                  onYearChange: customFilters.onCn19YearChange,
                  onCurrencyChange: customFilters.onCn19CurrencyChange,
                  onEnterIbrsRates: customFilters.onCn19EnterIbrsRates,
                  isProcessing: customFilters.cn19IsProcessing,
                }} />
              )}

              {shouldShowCustomENO1Status && !isMobile && customFilters && (
                <ENO1StatusFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomE53Status && !isMobile && customFilters && (
                <E53StatusFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomE09Status && !isMobile && customFilters && (
                <E09StatusFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomE55Status && !isMobile && customFilters && (
                <E55StatusFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomE55 && !isMobile && customFilters && (
                <E55ComparisonFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomAccuracy && !isMobile && customFilters && (
                <AccuracyReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomItemsKg && !isMobile && customFilters && (
                <ItemsKgReportFilters customFilters={{
                  ...customFilters,
                  yearQuarterOptions: yearQuarterOptions
                }} />
              )}

              {shouldShowCustomIpcTdAccounting && !isMobile && customFilters && (
                <IpcTdAccountingFilters customFilters={{
                  ...customFilters,
                  yearOptions: years.filter(year => year !== 'all')
                }} />
              )}

              {shouldShowCustomStatus && !isMobile && customFilters && (
                <StyledSegmentedControl className="custom-status-filter">
                  {customStatusOptions.map((option) => (
                    <StyledSegmentButton
                      key={option.id}
                      active={customFilters.status === option.id}
                      onClick={() => customFilters.onStatusChange?.(option.id)}
                      sx={{ color: customFilters.status === option.id ? '#ffffff' : '#00B6BA' }}
                    >
                      {option.label}
                    </StyledSegmentButton>
                  ))}
                </StyledSegmentedControl>
              )}

              {isMobile ? (
                <MobileFilterButton onClick={handleToggleMobileFilter}>
                  <FontAwesomeIcon icon={faFilter} />
                  <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 700 }}>Filters</Typography>
                </MobileFilterButton>
              ) : (
                !shouldShowCustomNO2 && !shouldShowCustomNO3 && !shouldShowCustomNO4 && !shouldShowCustomCN54 && !shouldShowCustomCN54bis && !shouldShowCustomCN56 && !shouldShowCustomCN10 && !shouldShowCustomCN19 && !shouldShowCustomENO1Status && !shouldShowCustomE53Status && !shouldShowCustomE09Status && !shouldShowCustomE55Status && !shouldShowCustomE55 && !shouldShowCustomAccuracy && !shouldShowCustomItemsKg && !shouldShowCustomIpcTdAccounting && !shouldShowCustomStatus && renderFilterContent()
              )}

            </FilterContainer>
          )}
        </ContentContainer>

        {onBookmarkClick && (
          <StyledBookmarkButton onClick={onBookmarkClick}>
            <Typography>Bookmarks</Typography>
            <FontAwesomeIcon icon={faCaretDown} />
          </StyledBookmarkButton>
        )}
      </StyledContainer>

      {/* Mobile Filter Drawer - Only show if any filters are enabled */}
      {isMobile && shouldShowFilters && (
        <MobileFilterDrawer isOpen={isMobileFilterOpen} onClick={handleCloseMobileFilter}>
          <MobileFilterContent onClick={(e) => e.stopPropagation()}>
            <MobileFilterHeader>
              <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Filters</Typography>
              <IconButton onClick={handleCloseMobileFilter}>
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </MobileFilterHeader>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {shouldShowPpo && (
                <>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>DO</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: '150px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                    }}
                  >
                    {(customFilters?.isPpoLoading ?? isPposLoading) ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} color='info' />
                      </Box>
                    ) : (!customFilters?.ppoOptions && pposError) ? (
                      <Box sx={{ p: 2, color: 'error.main' }}>Error loading PPOs</Box>
                    ) : (
                      ppoOptions.map((option) => (
                        <Button
                          key={option.id}
                          onClick={() => handlePpoChange(option.id)}
                          sx={{
                            justifyContent: 'space-between',
                            padding: '8px 16px',
                            textAlign: 'left',
                            backgroundColor: tempPpoId === option.id ? '#e6f7f7' : 'transparent',
                            color: '#333',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          {option.label}
                        </Button>
                      ))
                    )}
                  </Box>
                </>
              )}

              {shouldShowStatus && (
                <>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Status</Typography>

                  {/* Status Filter */}
                  <StyledSegmentedControl>
                    {statusOptions.map((option) => (
                      <StyledSegmentButton
                        key={option.id}
                        active={tempStatusId === option.id}
                        onClick={() => handleStatusChange(option.id)}
                        sx={{ color: tempStatusId === option.id ? '#ffffff' : '#00B6BA' }}
                      >
                        {option.label}
                      </StyledSegmentButton>
                    ))}
                  </StyledSegmentedControl>
                </>
              )}

              {shouldShowType && (
                <>
                  {/* Type Filter */}
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', mt: 1 }}>Type</Typography>
                  <StyledSegmentedControl>
                    {typeOptions.map((option) => (
                      <StyledSegmentButton
                        key={option.id}
                        active={tempTypeId === option.id}
                        onClick={() => handleTypeChange(option.id)}
                        sx={{ color: tempTypeId === option.id ? '#ffffff' : '#00B6BA' }}
                      >
                        {option.label}
                      </StyledSegmentButton>
                    ))}
                  </StyledSegmentedControl>
                </>
              )}

              {shouldShowYear && (
                <>
                  {/* Year Filter */}
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', mt: 1 }}>Year</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: '150px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                    }}
                  >
                    {years.map((year) => (
                      <Button
                        key={year}
                        onClick={() => handleYearChange(year)}
                        sx={{
                          justifyContent: 'flex-start',
                          padding: '8px 16px',
                          textAlign: 'left',
                          backgroundColor: tempYearId === year ? '#e6f7f7' : 'transparent',
                          color: '#333',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        {year === 'all' ? 'All Years' : year}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            <MobileFilterActions>
              <Button
                fullWidth
                variant='outlined'
                onClick={handleResetFilters}
                sx={{
                  borderColor: '#00B6BA',
                  color: '#00B6BA',
                }}
              >
                Reset
              </Button>
              <Button
                fullWidth
                onClick={handleApplyFilters}
                sx={{
                  backgroundColor: '#00B6BA',
                  '&:hover': {
                    backgroundColor: '#00A2A6',
                  },
                }}
              >
                <Typography
                  sx={{
                    ...applyButtonTextStyle,
                    color: 'inherit', // Use the inherited color from the parent Button
                  }}
                >
                  Apply
                </Typography>
              </Button>
            </MobileFilterActions>
          </MobileFilterContent>
        </MobileFilterDrawer>
      )}


    </>
  );
};

export default SectionBar;
